import React, { useEffect, useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  ChartOptions,
} from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend, Title);

type Slice = { label: string; value: number; color: string };

export function DonutChart({
  title = "",
  slices,
}: {
  title?: string;
  slices: Slice[];
}) {
  const total = useMemo(
    () => {
      return slices.reduce((s, x) => s + (Number(x.value) || 0), 0);
    },
    [slices]
  );

  const [centerText, setCenterText] = useState<string | null>(null);
  const [centerValue, setCenterValue] = useState<string | number>(total);

  const data = useMemo(
    () => ({
      labels: slices.map((s) => s.label),
      datasets: [
        {
          data: slices.map((s) => s.value),
          backgroundColor: slices.map((s) => s.color),
          borderWidth: 1,
        },
      ],
    }),
    [slices]
  );

  const options = useMemo<ChartOptions<"doughnut">>(
    () => ({
      cutout: "75%",
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { enabled: true } },
      onHover: (_event, activeEls, chart) => {
        if (!activeEls?.length) {
          resetCenter();
          return;
        }
        const idx = activeEls[0].index;
        const label = chart.data.labels?.[idx] as string;
        const value = Number(chart.data.datasets[0].data[idx] || 0);
        onHoverLegend(label, value); // tái sử dụng hàm tính % + set center
      },
    }),
    [total]
  );

  const onHoverLegend = (label: string, value: number) => {
    setCenterText(label);
    const pct = total > 0 ? Math.round((value / total) * 100) + " %" : "0 %";
    setCenterValue(pct);
  };

  const resetCenter = () => {
    setCenterText(null);
    setCenterValue(total);
  };

  useEffect(() => {
    resetCenter();
  }, [total]);

  return (
    <div className="flex flex-wrap gap-6">
      <div className="bg-white p-4 border border-green-500 rounded-md flex flex-wrap gap-4 w-full">
        {/* chart */}
        <div className="relative min-w-[300px] flex-1 h-[260px] flex items-center justify-center">
          <Doughnut data={data} options={options} />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
            <p className="text-3xl">{centerValue}</p>
            <p className="text-xl text-slate-500">{centerText ?? title}</p>
          </div>
        </div>

        {/* legend */}
        <ul className="flex-1 min-w-[220px] flex flex-col justify-center gap-2">
          {slices.map((s, i) => (
            <li
              key={i}
              className="w-fit px-2 py-1 rounded hover:bg-slate-100 cursor-default flex items-center"
              onMouseEnter={() => onHoverLegend(s.label, s.value)}
              onMouseLeave={resetCenter}
            >
              <span
                className="w-4 h-4 mr-2 rounded-sm"
                style={{ backgroundColor: s.color }}
              />
              <span>{`${s.label}: ${s.value}`}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
